import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { isArrayWithItems } from "../../../helpers/array-helper";
import styles from "./job-opening-list.module.less";
import { TJobOpeningListItem } from "../../../types/job-opening-types";
import ShortcutCard from "../shortcut-card/shortcut-card";
import { useAnimateFadeIn } from "../../../animation/animation";
import { m as motion } from "framer-motion";

export type TJobOpeningListProps = {
  jobOpenings: Array<TJobOpeningListItem>;
  maxNumberOfJobOpenings?: number;
  fallbackText?: string;
};

const getJobOpeningsWithMax = (jobOpenings: Array<TJobOpeningListItem>, maxNumberOfJobOpenings: number) => {
  if (!isArrayWithItems(jobOpenings)) {
    return [];
  }

  if (maxNumberOfJobOpenings === 0) {
    return jobOpenings;
  }

  return jobOpenings.slice(0, maxNumberOfJobOpenings);
};

export const JobOpeningList = ({ jobOpenings, maxNumberOfJobOpenings = 6, fallbackText }: TJobOpeningListProps) => {
  const { t } = useTranslation();
  const fadeInAnimation = useAnimateFadeIn({
    whileInView: true,
    easing: "easeInOut",
    duration: 0.5,
    displayAmountTreshold: 0.5,
  });

  const jobOpeningsWithMax = getJobOpeningsWithMax(jobOpenings, maxNumberOfJobOpenings);

  const getLastApplicationDate = (date: string) => {
    const formattedDate = dayjs(date).format("YYYY-MM-DD");
    const currentDate = dayjs().format("YYYY-MM-DD");

    if (formattedDate === currentDate) {
      return `${t("JOB_APPLY_LAST_DAY")}: ${t("TODAY")}`;
    }
    return `${t("JOB_APPLY_LAST_DAY")}: ${formattedDate}`;
  };

  return (
    <div className={[styles.wrapper].join(" ")}>
      <div className={[styles.contentWrapper].join(" ")}>
        {isArrayWithItems(jobOpeningsWithMax) ? (
          <ul className={styles.list}>
            {jobOpeningsWithMax.map((item) => {
              return (
                <motion.li {...fadeInAnimation} key={item.name + "-" + item.location} className={styles.item}>
                  <ShortcutCard
                    linkUrl={item.url}
                    title={item.name}
                    eyebrow={item.location}
                    subTitle={item.applicationDeadline ? getLastApplicationDate(item.applicationDeadline) : ""}
                    target="_blank"
                  />
                </motion.li>
              );
            })}
          </ul>
        ) : (
          <div dangerouslySetInnerHTML={{ __html: fallbackText ?? "" }} />
        )}
      </div>
    </div>
  );
};

export default JobOpeningList;
